import React from "react";
import { Typography, Box, IconButton } from "@mui/material";
import {
  Iconography,
  IconographySizes,
  Button,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import "./TierManagement.scss";
import { useAppSelector, useAppDispatch } from "../../../../../../redux";
import {
  addPartnerTierSagaAction,
  getPartnerTierListSagaAction,
  updatePartnerTierListSagaAction,
  deletePartnerTierList,
  addPartnerTierListPayloadType,
  updatePartnerTierListPayloadType,
} from "../../../../../../redux/saga/partner-program/saga-actions";
import { getPartnerLicenceListSagaAction } from "../../../../../../redux/saga/partner-program/licence-management/saga-actions";
import { PartnerTierListType } from "../../../../../../redux/slices/partner-program/Types";
import PartnerLicenceManagement from "../licence-management";
import PartnerTierModal from "./partnerTierModal";
import PartnerRegionalMultipliers from "../regional-multipliers";
import EngagementCategory from "../engagement-category/index";
import EngagementConfiguration from "../engagement-configuration";
import { LoaderSizes } from "@hid-galaxy-ui/galaxy-react/components/Loader/loaderEnums";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import PartnerSettingTab from "../partner-setting/index";
import Benefits from "../../../partner-organizations/manage-partner/partner-program/benefits";

const TabPanel: React.FC<{
  index: number;
  value: number;
  children: React.ReactNode;
}> = ({ index, value, children }) => {
  if (value !== index) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
};

export function formatDate(iso?: string) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const datePart = d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} | ${timePart}`;
  } catch {
    return iso ?? "-";
  }
}

const PARTNER_PROGRAM_ID = 0;
type BenefitGroups = {
  [categoryName: string]: string[];
};

const PartnerTierManagement: React.FC<{
  activeTab: number;
  onTabChange?: (v: number) => void;
  /** Module-level view permission: hide mutating actions, keep read-only viewing. */
  readOnly?: boolean;
}> = ({ activeTab, readOnly = false }) => {
  const dispatch = useAppDispatch();
  const {
    partnerTierList,
    status: { partnerTierListLoading, deletePartnerTierLoading },
  } = useAppSelector((state) => state.partner_program);

  const { partnerLicenceList } = useAppSelector(
    (state) => state.partner_licenceProgram
  );

  const [showAdd, setShowAdd] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [editTier, setEditTier] = React.useState<
    PartnerTierListType[number] | null
  >(null);
  const [showView, setShowView] = React.useState(false);
  const [viewTier, setViewTier] = React.useState<
    PartnerTierListType[number] | null
  >(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteTierId, setDeleteTierId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (activeTab === 1 && partnerTierList?.length === 0) {
      dispatch(getPartnerTierListSagaAction(PARTNER_PROGRAM_ID));
    }
  }, [activeTab, dispatch]);

  React.useEffect(() => {
    if (activeTab === 2 && partnerLicenceList?.length === 0) {
      dispatch(getPartnerLicenceListSagaAction(PARTNER_PROGRAM_ID));
    }
  }, [activeTab, dispatch]);
  const onDeleteTier = (id: number) => {
    dispatch(deletePartnerTierList({ ID: id, setOpen: setDeleteModalOpen }));
    // dispatch(getPartnerTierListSagaAction(PARTNER_PROGRAM_ID));
  };

  const onDeleteTierClick = (id: number) => {
    setDeleteModalOpen(true);
    setDeleteTierId(id);
  };
  

  const onEditTier = (tier: PartnerTierListType[number]) => {
    setEditTier(tier);
    setShowEdit(true);
  };

  const onViewTier = (tier: PartnerTierListType[number]) => {
    setViewTier(tier);
    setShowView(true);
  };

  return (
    <div>
     
      <TabPanel index={0} value={activeTab}>
        <PartnerSettingTab readOnly={readOnly} />
      </TabPanel>
      <TabPanel index={1} value={activeTab}>
         {activeTab === 1 && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: "12px",
            marginBottom: "12px",
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "Roboto",
                fontStyle: "normal",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "20px",
                letterSpacing: "0.2px",
                color: "#0A0A0A",
                marginBottom: "5px",
              }}
            >
              Tier Management
            </h1>
            <Typography
              variant="h4"
              style={{
                fontFamily: "Roboto",
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "18px",
                letterSpacing: "0.2px",
                color: "#4A5565",
              }}
            >
              Configure partner program tiers
            </Typography>
          </div>
          <Button
            variant="primary"
            label="Add Tier"
            icon={<Iconography icon="plus" />}
            onClick={() => setShowAdd(true)}
            disabled={readOnly}
          />
        </div>
      )}
        {/* {partnerTierListLoading && (
          <div className="pp-loading-hint" style={{ marginBottom: 8 }}>
            Loading tier list…
          </div>
        )} */}

        <div className="pp-table" role="table" aria-label="Tier management">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.5fr 120px",
              gap: "8px",
              padding: "8px 16px",
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderBottom: "1px solid #E5E7EB",
              borderRadius: "6px 6px 0 0",
              fontFamily: "'Roboto'",
              fontWeight: 500,
              color: "#374151",
                fontSize: "14px",
            }}
            role="rowgroup"
          >
            <div className="pp-th col-tier" role="columnheader">
              Tier Name
            </div>
            <div className="pp-th col-min" role="columnheader">
              Min Points
            </div>
            <div className="pp-th col-max" role="columnheader">
              Max Points
            </div>
            <div className="pp-th col-desc" role="columnheader">
              Description
            </div>
            <div className="pp-th col-created" role="columnheader">
              Created At
            </div>
            <div className="pp-th col-actions" role="columnheader">
              Actions
            </div>
          </div>
          <div className="pp-table-body" role="rowgroup">
            {partnerTierListLoading ? (
              <div
                style={{
                  padding: "16px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Loader variant={LoaderSizes.medium} />
              </div>
            ) : (partnerTierList ?? []).length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                Tiers details not found
              </div>
            ) : (
              (partnerTierList ?? []).map((tier, index) => {
                const maxPointsNumeric =
                  tier.max_points !== null && !isNaN(Number(tier.max_points))
                    ? Number(tier.max_points)
                    : null;

                return (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1.5fr 120px",
                      gap: "8px",
                      alignItems: "center",
                       padding: "12px 16px",
                      minHeight: "48px",
                      
                      borderTop: index === 0 ? "none" : "2px solid #F3F4F6",
                    }}
                    key={tier.ID}
                    role="row"
                  >
                    <div className="pp-td col-tier" role="cell">
                      <Typography
                        variant="body1"
                        className="pp-tier-name"
                        style={{ paddingLeft: "1px" }}
                      >
                        {tier.tier_name}
                      </Typography>
                    </div>

                    <div className="pp-td col-min" role="cell">
                      {Number(tier.min_points).toLocaleString()}
                    </div>

                    <div className="pp-td col-max" role="cell">
                      {maxPointsNumeric !== null
                        ? maxPointsNumeric.toLocaleString()
                        : String(tier.max_points ?? "—")}
                    </div>
                    <div className="pp-td col-desc" role="cell">
                      <button
                        type="button"
                        className="pp-link"
                        aria-label={`View description and benefits for ${tier.tier_name}`}
                        onClick={() => onViewTier(tier)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#0D6EFD",
                          cursor: "pointer",
                          padding: 0,
                        }}
                      >
                        View
                      </button>
                    </div>

                    <div className="pp-td col-created" role="cell">
                      {formatDate(tier.created_at)}
                    </div>

                    <div className="pp-td col-actions" role="cell">
                      <IconButton
                        aria-label={`Edit ${tier.tier_name}`}
                        onClick={() => onEditTier(tier)}
                        disabled={readOnly}
                      >
                        <Iconography icon="pen" size={IconographySizes.Small} />
                      </IconButton>
                      <IconButton
                        aria-label={`Delete ${tier.tier_name}`}
                        onClick={() => onDeleteTierClick(tier.ID)}
                        disabled={readOnly}
                      >
                        <Iconography
                          icon="trashCan"
                          size={IconographySizes.Small}
                        />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        {showAdd && (
          <PartnerTierModal
            onClose={() => setShowAdd(false)}
            onSubmit={(payload: addPartnerTierListPayloadType) => {
              const clean: addPartnerTierListPayloadType = {
                ...payload,
                min_points: Number(payload.min_points),
                max_points:
                  (payload as any).max_points === "" ||
                  (payload as any).max_points === null
                    ? (null as any)
                    : (Number((payload as any).max_points) as any),
                created_at: new Date().toISOString(),
                update_at: new Date().toISOString(),
                short_description: payload.short_description?.trim(),
                long_description: payload.long_description?.trim(),
              } as any;
              dispatch(addPartnerTierSagaAction(clean));
              // dispatch(getPartnerTierListSagaAction(PARTNER_PROGRAM_ID));
            }}
            mode="add"
            open
          />
        )}
        {showEdit && editTier && (
          <PartnerTierModal
            onClose={() => {
              setShowEdit(false);
              setEditTier(null);
            }}
            onSubmit={(payload: updatePartnerTierListPayloadType) => {
              const clean = {
                ...payload,
                ID: editTier.ID,
                min_points: Number(payload.min_points),
                max_points:
                  (payload as any).max_points === "" ||
                  (payload as any).max_points === null
                    ? null
                    : Number((payload as any).max_points),
                update_at: new Date().toISOString(),
                short_description: payload.short_description?.trim(),
                long_description: payload.long_description?.trim(),
              } as any;
              dispatch(updatePartnerTierListSagaAction(clean));
              // dispatch(getPartnerTierListSagaAction(PARTNER_PROGRAM_ID));
            }}
            mode="edit"
            open
            initialValue={{
              ID: editTier.ID,
              tier_name: editTier.tier_name,
              min_points: Number(editTier.min_points),
              max_points:
                editTier.max_points && !isNaN(Number(editTier.max_points))
                  ? Number(editTier.max_points)
                  : null,
              short_description: editTier.short_description ?? "",
              long_description: editTier.long_description ?? "",
              created_at: editTier.created_at,
              update_at: new Date().toISOString(),
              benefits:
                (editTier as PartnerTierListType[number] & { tier_benefits?: BenefitGroups }).tier_benefits ??
                {},
            }}
          />
        )}
        {showView && viewTier && (
          <PartnerTierModal
            onClose={() => {
              setShowView(false);
              setViewTier(null);
            }}
            onSubmit={() => {}}
            mode="view"
            open
            initialValue={{
              ID: viewTier.ID,
              tier_name: viewTier.tier_name,
              short_description: viewTier.short_description ?? "",
              long_description: viewTier.long_description ?? "",
              created_at: viewTier.created_at,
              update_at: viewTier.updated_at ?? viewTier.created_at,
              benefits:
                (viewTier as PartnerTierListType[number] & { tier_benefits?: BenefitGroups }).tier_benefits ??
                {},
            }}
          />
        )}
        {deleteModalOpen && deleteTierId && (
          <DeleteModal
            setDeleteOpen={setDeleteModalOpen}
            onDeleteButtonClick={() => onDeleteTier(deleteTierId)}
            deleteButtonLoading={deletePartnerTierLoading}
            optionalHeader="Delete Tier"
            message="Are you sure you want to delete this tier?"
          />
        )}
      </TabPanel>
      <TabPanel index={2} value={activeTab}>
        <PartnerLicenceManagement
          activeTab={activeTab}
          partnerProgramId={PARTNER_PROGRAM_ID}
          readOnly={readOnly}
        />
      </TabPanel>

      <TabPanel index={3} value={activeTab}>
        <PartnerRegionalMultipliers
          activeTab={activeTab}
          partnerProgramId={PARTNER_PROGRAM_ID}
          readOnly={readOnly}
        />
      </TabPanel>

      <TabPanel index={4} value={activeTab}>
        <EngagementCategory activeTab={activeTab} readOnly={readOnly} />
      </TabPanel>

      <TabPanel index={5} value={activeTab}>
        <EngagementConfiguration
          activeTab={activeTab}
          partnerProgramId={PARTNER_PROGRAM_ID}
          readOnly={readOnly}
        />
      </TabPanel>

      <TabPanel index={6} value={activeTab}>
        <Benefits showQuickActions={false} readOnly={readOnly} />
      </TabPanel>
    </div>
  );
};

export default PartnerTierManagement;
